import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Check, Loader2, Users, X } from "lucide-react";
import { Avatar } from "@/components/atoms/Avatar";
import { Button } from "@/components/atoms/Button";
import { CopyButton } from "@/components/atoms/CopyButton";
import { Badge } from "@/components/atoms/Badge";
import { Input } from "@/components/atoms/Input";
import { Tooltip } from "@/components/atoms/Tooltip";
import { TextArea } from "@/components/atoms/TextArea";
import { SearchInput } from "@/components/molecules/SearchInput";
import { EntityBranchSelect } from "@/components/molecules/ScopePicker";
import { Drawer } from "@/components/organisms/Drawer";
import { contactsService } from "@/services/contactsService";
import { contactDetailsToSummary } from "@/modules/contacts/contactFormUtils";
import { labelContactIds, labelsService, type LabelSummary } from "@/services/labelsService";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSettingsStore } from "@/store/settingsStore";
import { cn } from "@/lib/cn";
import { getApiErrorMessage } from "@/utils/apiErrors";
import type { ContactSummary } from "@/types/entities";

interface Props {
  open: boolean;
  /** The label being edited, or null/undefined to create a new one. */
  label?: LabelSummary | null;
  /**
   * Where a new label is filed when the Labels page has narrowed the list.
   * Edits still open in the label's own entity and branch.
   */
  defaultCreateScope?: { legalEntityId: string | null; branchId: string };
  onClose: () => void;
  /** Fired after a successful save, so the list behind the modal can refresh. */
  onSaved?: () => void;
}

/** How many contacts one page of the picker holds. */
const PAGE_SIZE = 50;

const contactLabel = (contact: ContactSummary) =>
  contact.name?.trim() ||
  [contact.forename, contact.surname].filter(Boolean).join(" ").trim() ||
  contact.email?.trim() ||
  "(unnamed)";

/**
 * The saveable state of the form, as one comparable string. Contacts are sorted
 * because the order they were ticked in is not part of the membership, and the
 * text fields are trimmed because saving trims them too — so trailing
 * whitespace alone is not a change worth enabling the button for.
 */
const formSnapshot = (fields: {
  name: string;
  description: string;
  branchId: string;
  contactIds: string[];
}) =>
  JSON.stringify({
    name: fields.name.trim(),
    description: fields.description.trim(),
    branchId: fields.branchId,
    contactIds: [...fields.contactIds].sort(),
  });

/**
 * Creates a label, or edits the one it is given. Both flows share the same form
 * — an update replaces the label's name and its whole membership, so the fields
 * it needs are exactly the ones creation asks for.
 *
 * Which label is edited is the caller's business: the Labels page opens this
 * from a row, so the form no longer carries a picker of its own.
 */
export function LabelFormDrawer({ open, label, defaultCreateScope, onClose, onSaved }: Props) {
  const { legalEntityId: accountLegalEntityId, branchId: accountBranchId } = useSettingsStore();

  /**
   * Where the label lands. Seeded from the account context but editable here: a user who
   * can reach several entities would otherwise have to leave the modal, change the global
   * scope, and start the form again just to file a label under a different branch.
   */
  const [scope, setScope] = useState<{ legalEntityId: string | null; branchId: string }>({
    legalEntityId: accountLegalEntityId,
    branchId: accountBranchId ?? "",
  });
  const { legalEntityId, branchId } = scope;

  const [loadingMembers, setLoadingMembers] = useState(false);
  const editing = Boolean(label);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  /**
   * Whole contacts, not just ids — a selected contact can scroll out of the
   * result set once the search changes, and the chips still have to name it so
   * it stays removable.
   */
  const [selected, setSelected] = useState<ContactSummary[]>([]);
  /**
   * The contacts the label held when it was opened, kept as their own list so
   * they stay pinned at the top of the picker even after being unticked —
   * otherwise removing one by mistake would make the row vanish, since it need
   * not appear in any page of the list that has been fetched.
   */
  const [pinned, setPinned] = useState<ContactSummary[]>([]);
  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; contacts?: string; scope?: string }>({});
  /**
   * The form as it stood once it had finished loading, so an edit can tell
   * whether anything has actually been changed. Null until the prefill lands —
   * up to then there is nothing to compare against.
   */
  const [baseline, setBaseline] = useState<string | null>(null);

  /** Last page fetched, and a ticket so a slow page can't overwrite a newer search. */
  const pageRef = useRef(0);
  const requestSeq = useRef(0);
  /** Ticket for member hydration, so a slow label can't overwrite a newer pick. */
  const memberSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setErrors({});
    if (label) {
      // An edit opens in the label's own scope, not the account's — the branch it
      // was filed under is what decides which contacts it may hold.
      setScope({
        legalEntityId: label.legalEntityId ?? accountLegalEntityId,
        // "0" is the entity-wide sentinel the create call writes; it is not a branch.
        branchId: label.branchId && label.branchId !== "0" ? label.branchId : "",
      });
      setName(label.name ?? "");
      setDescription(label.labelDescription ?? "");
      setSelected([]);
      setPinned([]);
      // Nothing to compare against until the membership arrives.
      setBaseline(null);
      void loadLabelMembers(label);
      return;
    }
    setName("");
    setDescription("");
    setSelected([]);
    setPinned([]);
    // Each opening starts from the account context again, so an edit made for one label
    // does not silently become the default for the next.
    const createScope = defaultCreateScope ?? {
      legalEntityId: accountLegalEntityId,
      branchId: accountBranchId ?? "",
    };
    setScope(createScope);
    // A new label starts empty, so the empty form is what "unchanged" means here.
    setBaseline(formSnapshot({ name: "", description: "", branchId: createScope.branchId, contactIds: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, label]);

  // Contacts of the selected scope. Searching is done by the API rather than
  // over the fetched page — a legal entity can hold far more contacts than one
  // page, so filtering locally would only ever search the first slice of them.
  useEffect(() => {
    // An edit's scope is fixed — it is seeded from the label above and the picker
    // is locked — so this must not wipe the prefill it just landed.
    if (label) return;
    // Anyone picked under the previous scope is dropped: the label is saved
    // for this legal entity and branch, so carrying over selections the user
    // can no longer see would silently attach the wrong people to it.
    setSelected([]);
    setPinned([]);
    setName("");
    setDescription("");
    // The form was just cleared back to empty, so that is the new baseline.
    setBaseline(formSnapshot({ name: "", description: "", branchId, contactIds: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntityId, branchId]);

  const fetchContacts = useCallback(
    async (page: number, append: boolean) => {
      if (!legalEntityId) return;
      const ticket = (requestSeq.current += 1);
      if (append) setLoadingMore(true);
      else setLoadingContacts(true);

      try {
        const res = await contactsService.list(
          {
            ...(branchId
              ? { legalEntityId, branchIds: branchId }
              : { legalEntityIds: legalEntityId }),
            skipGlobalContext: true,
            ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
          },
          { page, size: PAGE_SIZE, sort: "atlasModifiedAt", direction: "desc" },
        );
        // A newer search started while this was in flight — drop the result
        // rather than letting a stale page overwrite it.
        if (ticket !== requestSeq.current) return;

        const batch = res.content ?? [];
        pageRef.current = page;
        setContacts((prev) => (append ? [...prev, ...batch] : batch));
        setTotalContacts(res.totalElements ?? 0);
        setHasMore(page + 1 < (res.totalPages ?? 0));
      } catch (error) {
        if (ticket !== requestSeq.current) return;
        if (!append) {
          setContacts([]);
          setTotalContacts(0);
        }
        setHasMore(false);
        toast.error(getApiErrorMessage(error) ?? "Failed to load contacts for this scope.");
      } finally {
        if (ticket === requestSeq.current) {
          setLoadingContacts(false);
          setLoadingMore(false);
        }
      }
    },
    [legalEntityId, branchId, debouncedSearch],
  );

  // First page — refetched from scratch whenever the scope or the search changes.
  useEffect(() => {
    if (!open || !legalEntityId) {
      setContacts([]);
      setTotalContacts(0);
      setHasMore(false);
      return;
    }
    pageRef.current = 0;
    void fetchContacts(0, false);
  }, [open, legalEntityId, fetchContacts]);

  /**
   * Loads the contacts a label groups, as the ticked rows the form edits.
   *
   * `/contacts/by-labels` is asked who they are rather than reading the row: the
   * listing carries a count but not always the ids. That endpoint answers with
   * ids alone, so each one is then named — from the page of contacts already
   * loaded where possible, and fetched individually otherwise, since an id on
   * its own would render as an unreadable row.
   *
   * The row's own ids are still the fallback, so a label the endpoint cannot
   * answer for is not silently emptied — saving replaces the membership with
   * whatever is on screen.
   */
  async function loadLabelMembers(label: LabelSummary) {
    const ticket = (memberSeq.current += 1);
    setLoadingMembers(true);
    try {
      let ids: string[] = [];
      try {
        ids = await contactsService.listContactIdsByLabels([String(label.id)]);
      } catch (error) {
        toast.error(
          getApiErrorMessage(error) ?? "Failed to load the contacts this label groups.",
          { toastId: "label-members-error" },
        );
      }
      if (ticket !== memberSeq.current) return;
      if (ids.length === 0) ids = labelContactIds(label);
      const labelBaseline = (contactIds: string[]) =>
        formSnapshot({
          name: label.name ?? "",
          description: label.labelDescription ?? "",
          branchId: label.branchId && label.branchId !== "0" ? label.branchId : "",
          contactIds,
        });

      if (ids.length === 0) {
        setSelected([]);
        setPinned([]);
        setBaseline(labelBaseline([]));
        return;
      }

      const known = new Map(contacts.map((c) => [c.id, c]));
      const resolved = await Promise.all(
        ids.map(async (id) => {
          const cached = known.get(id);
          if (cached) return cached;
          try {
            const details = await contactsService.getOne(id, legalEntityId ?? undefined);
            // The detail payload keeps email and phone on `person[0]` rather than
            // flattened onto the record the way the listing does - handing the raw
            // shape to a row built for the listing loses both.
            return { ...contactDetailsToSummary(details), id };
          } catch {
            // A contact that can no longer be read still belongs to the label,
            // so it stays selected — and removable — under a placeholder name.
            return { id, name: "(unavailable contact)" } as ContactSummary;
          }
        }),
      );
      if (ticket !== memberSeq.current) return;
      setSelected(resolved);
      setPinned(resolved);
      setBaseline(labelBaseline(resolved.map((c) => c.id)));
    } finally {
      if (ticket === memberSeq.current) setLoadingMembers(false);
    }
  }

  /**
   * The picker list, with the label's own contacts at the top and ticked. Its
   * membership is loaded whole, but the list below is only the pages fetched so
   * far — without this, a member sitting on page 7 would look unselected, and
   * seeing who is in the label would mean scrolling the whole entity.
   *
   * Pinned rows are matched against the search box here rather than by the API:
   * they are already in memory, and the search only narrows which of them the
   * list shows, never which stay selected.
   */
  const listedContacts = useMemo(() => {
    if (pinned.length === 0) return contacts;
    const needle = debouncedSearch.trim().toLowerCase();
    const matchesSearch = (contact: ContactSummary) =>
      !needle ||
      [contactLabel(contact), contact.email, contact.mobilePhone].some((field) =>
        field?.toLowerCase().includes(needle),
      );

    const top = pinned.filter(matchesSearch);
    const topIds = new Set(top.map((c) => c.id));
    return [...top, ...contacts.filter((c) => !topIds.has(c.id))];
  }, [pinned, contacts, debouncedSearch]);

  /**
   * The drawer is still filling itself in: the label's membership is on its way,
   * or the first page of contacts is, with nothing on screen to pick from yet.
   * A later search or a scroll refetch is not this — those keep the list they
   * already have, so they get the small inline spinners instead of blanking the
   * whole form.
   */
  const hydrating = loadingMembers || (loadingContacts && contacts.length === 0);

  /**
   * Whether the form differs from what it was loaded with. Saving an untouched
   * label is a no-op that still replaces its whole membership server-side, so
   * the button stays out of reach until there is something to save.
   */
  const dirty = useMemo(() => {
    if (baseline === null) return false;
    return (
      baseline !==
      formSnapshot({ name, description, branchId, contactIds: selected.map((c) => c.id) })
    );
  }, [baseline, name, description, branchId, selected]);

  /**
   * Why the save button is out of reach, phrased for the tooltip — a disabled
   * button that says nothing just reads as broken. Null when it is enabled.
   */
  const disabledReason = useMemo(() => {
    if (hydrating) {
      return editing
        ? "Still loading this label and the contacts it groups."
        : "Still loading contacts.";
    }
    // The required fields come first and are named individually, so the tooltip
    // says which one is missing rather than restating the whole form. They are
    // checked in the order they appear on screen.
    const missing: string[] = [];
    if (!legalEntityId) missing.push("choose a legal entity");
    if (!name.trim()) missing.push("enter a label name");
    if (selected.length === 0) missing.push("pick at least one contact");
    if (missing.length > 0) {
      const list =
        missing.length === 1
          ? missing[0]
          : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
      return `Before saving, ${list}.`;
    }
    if (!dirty) {
      return editing
        ? "Nothing has changed yet — edit the name, description or contacts to save."
        : "Nothing to save yet — name the label and pick the contacts it groups.";
    }
    return null;
  }, [hydrating, editing, legalEntityId, name, selected, dirty]);

  /** Loads the next page once the user reaches the end of the list. */
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || loadingContacts || loadingMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) return;
    void fetchContacts(pageRef.current + 1, true);
  };

  const toggleContact = (contact: ContactSummary) => {
    setSelected((prev) =>
      prev.some((c) => c.id === contact.id)
        ? prev.filter((c) => c.id !== contact.id)
        : [...prev, contact],
    );
    setErrors((prev) => (prev.contacts ? { ...prev, contacts: undefined } : prev));
  };

  async function handleSubmit() {
    if (saving) return;

    const next: typeof errors = {};
    if (!name.trim()) next.name = "Label name is required.";
    if (selected.length === 0) next.contacts = "Select at least one contact.";
    if (!legalEntityId) next.scope = "Select a legal entity before saving a label.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    // "0" is the sentinel for a legal-entity-wide label — "All branches"
    // chosen in the picker above.
    const payload = {
      name: name.trim(),
      // Optional — sent as an empty string when cleared, so an update actually
      // removes a description rather than leaving the old one in place.
      labelDescription: description.trim(),
      branchId: branchId || "0",
      contactIds: selected.map((c) => c.id),
    };

    setSaving(true);
    try {
      if (label) {
        await labelsService.update(label.id, payload);
        toast.success(`Label "${payload.name}" updated.`);
      } else {
        await labelsService.create([{ ...payload, legalEntityId: legalEntityId! }]);
        toast.success(`Label "${payload.name}" created.`);
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error) ??
          `Failed to ${editing ? "update" : "create"} the label. Please try again.`,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? label?.name ?? "Label" : "New Label"}
      subtitle={
        editing
          ? (
            <span className="group/copy inline-flex items-center gap-1">
              <span>ID: {label?.id}</span>
              <CopyButton
                value={label?.id}
                label="Label ID"
                successMessage="Record ID copied to clipboard"
              />
            </span>
          )
          : "Name the label and pick the contacts it groups."
      }
      width="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {/* The tooltip hangs off a wrapper, not the button itself: a disabled
              button fires no mouse events, so it could never reveal its own
              explanation. */}
          {/* Opens to the left: the button sits in the drawer's bottom-right
              corner, so a bubble centred above it runs off the viewport. */}
          <Tooltip side="left" content={disabledReason ?? ""} disabled={!disabledReason}>
            <span className="inline-flex">
              <Button
                variant="primary"
                onClick={() => void handleSubmit()}
                loading={saving}
                // Saving replaces the whole membership, so it must not be possible
                // before the form knows what that membership currently is — nor
                // when nothing has been changed.
                disabled={Boolean(disabledReason)}
              >
                {editing ? "Save Changes" : "Create Label"}
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      {hydrating ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center gap-3">
          <Loader2 size={24} className="animate-spin text-bridge-accent" />
          <p className="text-sm text-bridge-muted">
            {editing ? "Loading the label and its contacts…" : "Loading contacts…"}
          </p>
        </div>
      ) : (
      <div className="space-y-5">
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            Legal Entity &amp; Branch<span className="ml-0.5 text-bridge-danger">*</span>
          </div>
          <EntityBranchSelect
            legalEntityId={legalEntityId}
            branchId={branchId}
            onChange={(next) => {
              setScope({ legalEntityId: next.legalEntityId, branchId: next.branchId });
              if (errors.scope) setErrors((prev) => ({ ...prev, scope: undefined }));
            }}
            invalid={Boolean(errors.scope)}
            disabled={editing}
            tooltip={
              editing
                ? "The scope a label was filed under. It is fixed once the label exists — the update call cannot move it, and its contacts belong to this scope."
                : "Choose which legal entity and branch this label belongs to — it also decides which labels you can edit and which contacts you can add."
            }
          />
          {errors.scope && <p className="mt-1 text-xs text-bridge-danger">{errors.scope}</p>}
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            Label Name<span className="ml-0.5 text-bridge-danger">*</span>
          </div>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            placeholder="e.g. VIP Customers"
            autoFocus
          />
          {errors.name && <p className="mt-1 text-xs text-bridge-danger">{errors.name}</p>}
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
            Description
          </div>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this label is for (optional)"
            rows={3}
            className="h-auto items-start py-2"
          />
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
              Contacts<span className="ml-0.5 text-bridge-danger">*</span>
            </span>
            {selected.length > 0 && <Badge tone="accent">{selected.length} selected</Badge>}
          </div>

          <SearchInput value={search} onChange={setSearch} placeholder="Search contacts by name, email..." />

          {/* Selected contacts stay visible and removable even when the current
              search doesn't include them. */}
          {selected.length > 0 && (
            <div className="mt-2 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto scrollbar-metal">
              {selected.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => toggleContact(contact)}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-bridge-accent/60 bg-bridge-accent/10 px-2 py-1 text-xs font-medium text-bridge-accent transition-colors hover:bg-bridge-accent/20"
                >
                  <span className="truncate">{contactLabel(contact)}</span>
                  <X size={12} className="shrink-0" />
                </button>
              ))}
            </div>
          )}

          <div
            onScroll={handleListScroll}
            // The tallest thing in this form. The drawer scrolls as a whole, so the
            // cap is only there to stop the list swallowing the panel: capped against
            // the viewport, not just in rems, so a short window shrinks the list
            // instead of pushing the fields above it out of reach.
            className="mt-2 max-h-[min(24rem,42vh)] overflow-auto rounded-md border border-bridge-border/70 scrollbar-metal"
          >
            {loadingContacts && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-bridge-muted">
                <Loader2 size={15} className="animate-spin" />
                Loading contacts…
              </div>
            )}

            {!loadingContacts && listedContacts.length === 0 && (
              <div className="flex flex-col items-center gap-1 py-8 text-center">
                <Users size={18} className="text-bridge-subtle" />
                <p className="text-sm text-bridge-muted">
                  {!legalEntityId
                    ? "Select a legal entity to load contacts."
                    : search.trim()
                      ? "No contacts match your search."
                      : "No contacts found for this scope."}
                </p>
              </div>
            )}

            {!loadingContacts &&
              listedContacts.map((contact) => {
                const isSelected = selected.some((c) => c.id === contact.id);
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => toggleContact(contact)}
                    className={cn(
                      "flex w-full items-center gap-3 border-b border-bridge-border/50 px-3 py-2.5 text-left transition-colors last:border-b-0",
                      isSelected ? "bg-bridge-accent/10" : "hover:bg-bridge-bg",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                        isSelected
                          ? "border-bridge-accent bg-bridge-accent text-white"
                          : "border-bridge-border bg-white",
                      )}
                    >
                      {isSelected && <Check size={12} strokeWidth={3} />}
                    </span>
                    <Avatar name={contactLabel(contact)} size="sm" colorful className="shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-bridge-fg">
                        {contactLabel(contact)}
                      </span>
                      <span className="block truncate text-xs text-bridge-muted">
                        {contact.email || contact.mobilePhone || "no contact details"}
                      </span>
                    </span>
                    {contact.category && <Badge tone="neutral">{contact.category}</Badge>}
                  </button>
                );
              })}

            {loadingMore && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-bridge-muted">
                <Loader2 size={13} className="animate-spin" />
                Loading more…
              </div>
            )}
          </div>

          {!loadingContacts && listedContacts.length > 0 && (
            <p className="mt-1 text-xs text-bridge-muted">
              Showing {contacts.length} of {totalContacts}
              {hasMore ? " — scroll for more" : ""}
            </p>
          )}
          {errors.contacts && <p className="mt-1 text-xs text-bridge-danger">{errors.contacts}</p>}
        </div>
      </div>
      )}
    </Drawer>
  );
}
